import { Client } from '@sr-sdks/api-sdk-axios';
import { FEATURE_FLAGS } from '../../constant/featureFlags';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { EntityCard } from '../EntityCard';
import { EntityCardViewAction } from '../EntityCardViewAction';
import {
  formatClientIdLabel,
  formatRepresentationPeriod,
  formatRepresentationStatus,
} from './clientMeta';

interface ClientCardProps {
  /** Workspace base path, e.g. `/workspace/<id>`. */
  basePath: string;
  client: Client;
}

export const ClientCard = ({ basePath, client }: ClientCardProps) => {
  // The summary route requires MSR3_CLIENT_PAGE_ROUTE and
  // MSR3_CLIENT_SUMMARY_ROUTE both on (see WorkspaceRoutes), so only render
  // the View link once we can prove it won't just bounce the user back here.
  const [isClientSummaryRouteEnabled] = useFeatureFlag(
    FEATURE_FLAGS.MSR3_CLIENT_SUMMARY_ROUTE,
  );
  const clientIdLabel = formatClientIdLabel(client.clientReferenceId);
  const representationStatus = formatRepresentationStatus(
    client.representationStatus,
  );
  const representationPeriod = formatRepresentationPeriod(
    client.representationStartDate,
    client.representationEndDate,
  );
  const summaryPath = `${basePath}/clients/summary/${client._id}`;

  return (
    <EntityCard
      className="flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4"
      data-testid="client-card"
    >
      <EntityCard.Content className="w-full min-w-0 sm:w-auto">
        <EntityCard.Icon classes={{ wrapper: 'h-16' }} icon="person" />

        <EntityCard.Info className="min-w-0">
          <EntityCard.Title className="truncate">
            {client.clientName}
          </EntityCard.Title>

          {clientIdLabel && (
            <EntityCard.Subtitle>{clientIdLabel}</EntityCard.Subtitle>
          )}
        </EntityCard.Info>
      </EntityCard.Content>

      <EntityCard.Actions>
        {(representationStatus || representationPeriod) && (
          <EntityCard.Metadata>
            {representationPeriod && (
              <EntityCard.MetadataItem
                label="Representation Period"
                value={representationPeriod}
              />
            )}

            {representationStatus && (
              <EntityCard.MetadataItem
                label="Status"
                value={representationStatus}
              />
            )}
          </EntityCard.Metadata>
        )}

        {isClientSummaryRouteEnabled && (
          <EntityCardViewAction viewUrl={summaryPath} />
        )}
      </EntityCard.Actions>
    </EntityCard>
  );
};
